import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { MdClose, MdChevronLeft, MdChevronRight } from "react-icons/md";
import "./FieldInfoStepModal.css";

const STEP_DURATION_MS = 4000;

/** STEP 인덱스(0-based) → 게이지 % */
export function getGaugeForStep(stepIndex, totalSteps) {
  if (totalSteps <= 1) return 100;
  return (stepIndex / (totalSteps - 1)) * 100;
}

/**
 * 광고등록 필드 안내 — STEP 슬라이드 + 자동 게이지
 * - 이미지: 캐러셀 슬라이드
 * - 텍스트: 닷 위 고정 영역 (슬라이드와 분리)
 */
const FieldInfoStepModal = ({
  open = false,
  onClose,
  title = "입력 안내",
  steps = [],
  stepDurationMs = STEP_DURATION_MS,
}) => {
  const totalSteps = steps.length;
  const [currentStep, setCurrentStep] = useState(0);
  const [gaugeProgress, setGaugeProgress] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const resetState = useCallback(() => {
    setCurrentStep(0);
    // 1 STEP이면 전환이 없으므로 게이지를 100으로 고정
    setGaugeProgress(totalSteps <= 1 ? 100 : 0);
    setAutoPlay(totalSteps > 1);
  }, [totalSteps]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (!open || !autoPlay || totalSteps <= 1) return;
    if (currentStep >= totalSteps - 1) return;

    const startGauge = getGaugeForStep(currentStep, totalSteps);
    const endGauge = getGaugeForStep(currentStep + 1, totalSteps);
    const startTime = performance.now();
    let rafId;

    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / stepDurationMs, 1);
      setGaugeProgress(startGauge + (endGauge - startGauge) * t);

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        if (nextStep >= totalSteps - 1) {
          setAutoPlay(false);
        }
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [open, autoPlay, currentStep, totalSteps, stepDurationMs]);

  const goToStep = (stepIndex) => {
    if (stepIndex < 0 || stepIndex >= totalSteps) return;
    setAutoPlay(false);
    setCurrentStep(stepIndex);
    setGaugeProgress(getGaugeForStep(stepIndex, totalSteps));
  };

  const handlePrev = () => goToStep(currentStep - 1);
  const handleNext = () => goToStep(currentStep + 1);

  if (!open || totalSteps === 0) return null;

  const activeStep = steps[currentStep] || {};

  return createPortal(
    <div
      className="field-info-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="field-info-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="field-info-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="field-info-gauge-wrap">
          <div
            className="field-info-gauge-fill"
            style={{ width: `${gaugeProgress}%` }}
          />
          {totalSteps > 1 &&
            Array.from({ length: totalSteps }, (_, i) => (
              <span
                key={i}
                className="field-info-gauge-marker"
                style={{ left: `${getGaugeForStep(i, totalSteps)}%` }}
                aria-hidden
              />
            ))}
        </div>

        <header className="field-info-header">
          <div className="field-info-header-text">
            <h2 id="field-info-title">{title}</h2>
            <p className="field-info-step-label">
              STEP {currentStep + 1} / {totalSteps}
            </p>
          </div>
          <button
            type="button"
            className="field-info-close"
            aria-label="닫기"
            onClick={onClose}
          >
            <MdClose size={20} />
          </button>
        </header>

        {/* 이미지 캐러셀만 */}
        <div className="field-info-viewport">
          <div
            className="field-info-track"
            style={{ transform: `translateX(-${currentStep * 100}%)` }}
          >
            {steps.map((step, index) => (
              <article
                key={step.id || index}
                className="field-info-slide"
                aria-hidden={index !== currentStep}
              >
                {step.image ? (
                  <div className="field-info-image-wrap">
                    <img
                      src={step.image.src}
                      alt={step.image.alt || ""}
                      width={step.image.width}
                      height={step.image.height}
                      className="field-info-image"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="field-info-image-empty" aria-hidden />
                )}
              </article>
            ))}
          </div>
        </div>

        {/* 텍스트 고정 영역 (캐러셀 닷 위) */}
        <div className="field-info-caption">
          {activeStep.title && (
            <h3 className="field-info-slide-title">{activeStep.title}</h3>
          )}
          {activeStep.description && (
            <p className="field-info-slide-desc">{activeStep.description}</p>
          )}
        </div>

        <footer className="field-info-footer">
          <button
            type="button"
            className="field-info-nav-btn"
            aria-label="이전 STEP"
            disabled={currentStep <= 0}
            onClick={handlePrev}
          >
            <MdChevronLeft size={24} />
          </button>
          <div className="field-info-dots" role="tablist" aria-label="STEP">
            {steps.map((_, index) => (
              <button
                key={index}
                type="button"
                role="tab"
                aria-selected={index === currentStep}
                aria-label={`STEP ${index + 1}`}
                className={`field-info-dot ${
                  index === currentStep ? "is-active" : ""
                }`}
                onClick={() => goToStep(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className="field-info-nav-btn"
            aria-label="다음 STEP"
            disabled={currentStep >= totalSteps - 1}
            onClick={handleNext}
          >
            <MdChevronRight size={24} />
          </button>
        </footer>
        <button type="button" className="field-info-dismiss" onClick={onClose}>
          닫기
        </button>
      </div>
    </div>,
    document.body
  );
};

export default FieldInfoStepModal;
